import React from 'react';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { toast } from 'react-toastify';
import type { DropdownOption } from '../../../../types/DesignSystem/Dropdown';
import { EnvironmentGuideFormData } from '../Types/env.types';

interface EnvironmentGuideTabProps {
  editData: EnvironmentGuideFormData;
  onFormChange: (data: EnvironmentGuideFormData) => void;
  isCompatioAppsEnabled: boolean;
  onShowDisableDialog: () => void;
}

const DEFAULT_BUILDER_NAVIGATION = 'Yes';
const DEFAULT_MENU_NAVIGATION_NAME = 'Part Configuration';
const MAX_MENU_NAME_LENGTH = 100;

const EnvironmentGuideTab: React.FC<EnvironmentGuideTabProps> = ({
  editData,
  onFormChange,
  isCompatioAppsEnabled,
  onShowDisableDialog,
}) => {

  const handleEnableGuideChange = (checked: boolean) => {
    if (!checked) {
      onShowDisableDialog();
    } else {
      setValue('enable_guide', checked);
    }
  };

  const setValue = (key: keyof EnvironmentGuideFormData, value: any) => {
    onFormChange({
      ...editData,
      [key]: value,
    });
  };

  const handleBuilderNavigationUseDefaultChange = (checked: boolean) => {
    if (checked) {
      onFormChange({
        ...editData,
        guide_builder_navigation: DEFAULT_BUILDER_NAVIGATION,
      });
    }
  };

  const handleMenuNavigationNameUseDefaultChange = (checked: boolean) => {
    if (checked) {
      onFormChange({
        ...editData,
        guide_menu_navigation_name: DEFAULT_MENU_NAVIGATION_NAME,
      });
    }
  };

  const handleBuilderNavigationChange = (value: string | number) => {
    onFormChange({
      ...editData,
      guide_builder_navigation: value.toString(),
    });
  };

  const handleMenuNavigationNameChange = (value: string) => {
    if (value.length <= MAX_MENU_NAME_LENGTH) {
      onFormChange({
        ...editData,
        guide_menu_navigation_name: value,
      });
    }
  };

  const handleCopyToClipboard = (text: string, fieldName: string) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${fieldName} copied to clipboard!`);
    }).catch(() => {
      toast.error(`Failed to copy ${fieldName}`);
    });
  };

  const isBuilderNavigationYes = editData.guide_builder_navigation === 'Yes';

  // Check if current values match defaults
  const isBuilderNavigationDefault = editData.guide_builder_navigation === DEFAULT_BUILDER_NAVIGATION;
  const isMenuNavigationNameDefault = editData.guide_menu_navigation_name === DEFAULT_MENU_NAVIGATION_NAME;

  return (
    <div className="tw-space-y-6">
      <FieldTitle label="Script Configuration" />

      <TextField
        label="Guide JS Script URL"
        placeholder="Enter URL"
        value={editData.guide_js_script_url}
        onChange={(value) => setValue('guide_js_script_url', value)}
        showCopyButton={true}
        onCopy={() => handleCopyToClipboard(editData.guide_js_script_url || '', 'Guide Script URL')}
        infoMessage="JavaScript file that loads the Guide interactive questionnaire features on your e-commerce site."
        disabled
      />

      <div className="tw-flex tw-items-center tw-justify-between">
        <FieldTitle label="Enable Guide" />
        <Toggle
          label=""
          checked={editData.enable_guide}
          onChange={handleEnableGuideChange}
          disabled={!isCompatioAppsEnabled}
        />
      </div>

      {editData.enable_guide && (
        <>
          <Dropdown
            label="Builder Navigation"
            placeholder="Select option"
            value={editData.guide_builder_navigation}
            onChange={handleBuilderNavigationChange}
            disabled={!isCompatioAppsEnabled}
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ] as DropdownOption[]}
            infoMessage={
              <>
                Control whether a navigation menu for product builders appears on your website&apos;s homepage.
                <br />
                Enabled: Shows builder navigation menu on the homepage.
                <br />
                Disabled: Hides builder navigation menu from the homepage.
              </>
            }
          />

          <Checkbox
            label="Use default value"
            checked={isBuilderNavigationDefault}
            onChange={handleBuilderNavigationUseDefaultChange}
            disabled={!isCompatioAppsEnabled}
          />

          <TextField
            label="Menu Navigation Name"
            placeholder="Enter name"
            value={editData.guide_menu_navigation_name}
            onChange={handleMenuNavigationNameChange}
            infoMessage="Customize the heading text for the builder navigation menu. This text will appear as the menu title on your homepage."
            disabled={!isCompatioAppsEnabled || !isBuilderNavigationYes}
          />

          <Checkbox
            label="Use default value"
            checked={isMenuNavigationNameDefault}
            onChange={handleMenuNavigationNameUseDefaultChange}
            disabled={!isCompatioAppsEnabled || !isBuilderNavigationYes}
          />
        </>
      )}
    </div>
  );
};

export default EnvironmentGuideTab;